import { useGIARequest } from "@apis/diamondDetail";
import { VDBText } from "@components/atoms";
import CustomWeb from "@components/atoms/Webview";
import { useSelector } from "react-redux";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { ProductCategory } from "@utils/constants";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Linking, Platform, TouchableOpacity, View } from "react-native";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { Diamond } from "@src/types/DiamondDetail";
import { isPdfUrl } from "../helpers";

export interface OpenReportProps {
  url?: string;
  resizeImage?: boolean;
  showMediaModel?: boolean;
  itemID?: number;
  certNum?: string;
  GIACertEnable?: boolean;
  isSmallDeviceWidth?: boolean;
  deviceWidth?: number;
  extraSmallImage?: boolean;
  showExtraBottomView?: boolean;
  mediaType?: string;
  linkedPairChildren?: Diamond[];
}

const OpenReportView = ({
  url,
  resizeImage,
  itemID = 0,
  certNum,
  GIACertEnable = false,
  isSmallDeviceWidth,
  deviceWidth = 0,
  extraSmallImage = false,
  mediaType = "",
  linkedPairChildren,
}: OpenReportProps) => {
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const theme = useTheme();
  const activeProduct = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const [updatedURL, setUpdatedURL] = useState(url);
  const isGemprintView =
    updatedURL?.toLowerCase().includes("gemprint") ||
    url?.toLowerCase().includes("gemprint");

  const isEnabled =
    GIACertEnable &&
    !!certNum &&
    [
      "www.gia.edu",
      "api.igi.org",
      "www.igi.org",
      "my.hrdantwerp.com",
      "agslab.com",
    ].some((domain) => updatedURL?.includes(domain)) &&
    [
      ProductCategory.DIAMOND,
      ProductCategory.LAB_GROWN_DIAMOND,
      ProductCategory.GEMSTONE,
    ].includes(activeProduct);

  const matchedChild = linkedPairChildren?.find(
    (child) => !!url && child?.cert_url === url
  );
  const isLinkedPairCert = !!matchedChild?.cert_num;
  const effectiveCertNum = matchedChild?.cert_num ?? certNum;

  const giaResponse = useGIARequest(
    itemID,
    isEnabled,
    isLinkedPairCert ? effectiveCertNum : undefined,
    isLinkedPairCert
  );
  const [loadingUrlExternalView, setLoadingUrlExternalView] = useState(true);

  const preFix =
    Platform.OS === "android"
      ? "https://docs.google.com/gview?embedded=true&url="
      : "";

  useEffect(() => {
    if (!isEnabled) {
      setLoadingUrlExternalView(false);
    }
  }, [isEnabled]);

  useEffect(() => {
    if (giaResponse.data) {
      const isPDF = !isPdfUrl(giaResponse?.data?.data?.imputed_cert_link);
      setUpdatedURL(
        isPDF
          ? preFix + giaResponse?.data?.data?.imputed_cert_link
          : giaResponse?.data?.data?.imputed_cert_link
      );
      setLoadingUrlExternalView(false);
    }
  }, [giaResponse.data]);

  return (
    <>
      {loadingUrlExternalView ? (
        <CenterContainer resizeImage={resizeImage}>
          <PaddingContainer>
            <VDBText
              textAlign="center"
              color={theme["core-05"]}
              type={typography["Body-R"]}
            >
              {t("productDetailPage.openReportMessage.text")}
            </VDBText>
          </PaddingContainer>
        </CenterContainer>
      ) : giaResponse?.data?.data.cert_type === "external_unsecured" &&
        Platform.OS === "web" ? (
        <>
          <TouchableOpacity
            onPress={() =>
              Linking.openURL(giaResponse?.data?.data.imputed_cert_link)
            }
            style={{ flex: 1 }}
          >
            <CenterContainer resizeImage={resizeImage}>
              <PaddingContainer>
                <VDBText
                  textAlign="center"
                  color={theme["core-05"]}
                  type={typography["Body-R"]}
                >
                  {t("productDetailPage.clicktoOpenReport.text")}
                </VDBText>
              </PaddingContainer>
            </CenterContainer>
          </TouchableOpacity>
        </>
      ) : (
        <View
          style={{
            width: "100%",
            height: "100%",
          }}
        >
          <CustomWeb
            isSmallDeviceWidth={isSmallDeviceWidth}
            deviceWidth={deviceWidth}
            resizeImage={resizeImage}
            url={updatedURL}
            extraSmallImage={extraSmallImage}
            enableReload={true}
            allowScaleScript={false}
            mediaType={isEnabled ? "" : mediaType}
            isClickable={!isGemprintView}
          />
        </View>
      )}
    </>
  );
};

const CenterContainer = styled.View<any>`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme, resizeImage }) =>
    resizeImage ? theme["core-03"] : theme["core-03"]};

  border-top-left-radius: 11px;
  border-top-right-radius: 11px;
  border-bottom-left-radius: 11px;
  border-bottom-right-radius: 11px;
  max-width: ${({ resizeImage, theme }) =>
    resizeImage && theme.isMobile ? "150px" : "100%"};
`;

const PaddingContainer = styled.View`
  margin: 10px;
`;

export default OpenReportView;
